import React, { useState, useMemo, useEffect, useRef } from "react";
import * as Yup from "yup";
import { Formik, Field } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import { useApolloClient, useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Typography from "@mui/material/Typography";

import useNotification from "../../hooks/notification";
import useMutation from "../../hooks/useMutation";
import { INVITE_USER_MUTATION, UPDATE_USER_ACCOUNT_MUTATION } from "../../constants/graphql";
import {
  CLIENTS_AND_PROJECTS_QUERY,
  COMPANY_USERS_QUERY,
  FETCH_USER,
} from "../../constants/graphql/queries";
import parseErrors, { mapKeys } from "../../utils/parseErrors";
import Input from "../../components/form/Input";
import CollapsibleSelect from "../../components/form/CollapsibleSelect";
import useAuth from "../../hooks/useAuth";
import ProjectAutocomplete, {
  clientIdFromAccessValue,
  isClientAccessValue,
} from "../../components/form/projects/ProjectAutocomplete";
import { permissions } from "../../constants/user";
import useRoles from "../../hooks/user/useRoles";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const parseEmails = emailString => {
  if (!emailString) return [];

  return emailString
    .split(/[,;]+/)
    .map(email => email.trim())
    .filter(email => email.length > 0);
};

const validateEmails = emailString => {
  const emails = parseEmails(emailString);

  if (emails.length === 0) {
    return "At least one email is required";
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const invalidEmails = emails.filter(email => !emailRegex.test(email));

  if (invalidEmails.length > 0) {
    return `Invalid email(s): ${invalidEmails.join(", ")}`;
  }

  return null;
};

const validationSchema = Yup.object().shape({
  userId: Yup.string().nullable(),
  emails: Yup.string()
    .required("At least one email is required")
    .test("valid-emails", "Invalid email format", function(value) {
      const error = validateEmails(value);
      return error ? this.createError({ message: error }) : true;
    }),
  role: Yup.string()
    .required("Role is required")
    .nullable(),
  projectIds: Yup.mixed()
    .nullable()
    .test("project-ids", "At least one project is required", value => {
      if (value === "all") return true;
      return Array.isArray(value) && value.length > 0;
    }),
});

const GROUPED_ROLES = role => {
  const internalStaff = [
    {
      value: "admin",
      label: "Admin",
      permission: permissions.admin,
      tooltip: "Full system access and administrative privileges.",
    },
    {
      value: "manager",
      label: "Warehouse Manager",
      permission: permissions.manager,
      tooltip:
        "Full access to inventory and ordering. Auto-assigned to new customers in their warehouse.",
    },
    {
      value: "inventory",
      label: "Warehouse Staff",
      permission: permissions.inventory,
      tooltip: "Access to inventory and fulfillment tools for daily warehouse tasks.",
    },
    {
      value: "billing_user",
      label: "Billing User",
      permission: permissions.billing_user,
      tooltip: "Read-only internal access to assigned project orders and billing reports.",
    },
  ].filter(v => v.permission >= permissions[role]);

  const externalStakeholders = [
    {
      value: "client",
      label: "Customer Admin",
      permission: permissions.client,
      tooltip: "Full access to inventory and ordering for their organization.",
    },
    {
      value: "employee",
      label: "Inventory Lead",
      permission: permissions.employee,
      tooltip: "Can view inventory and place orders, but cannot edit assets.",
    },
    {
      value: "viewer",
      label: "Inventory Viewer",
      permission: permissions.viewer,
      tooltip: "Can view inventory but does not have editing permissions.",
    },
    {
      value: "shopper",
      label: "Inventory Shopper",
      permission: permissions.shopper,
      tooltip: "Can place orders via the customer storefront only.",
    },
  ].filter(v => v.permission >= permissions[role]);

  return [
    ...(internalStaff.length > 0
      ? [{ subheader: true, label: "Internal Staff" }, ...internalStaff]
      : []),
    ...(externalStakeholders.length > 0
      ? [{ subheader: true, label: "External Stakeholders" }, ...externalStakeholders]
      : []),
  ];
};

const styles = {
  existingUserNotice: {
    border: "1px solid rgba(0,0,0,0.12)",
    borderRadius: 1,
    px: 2,
    py: 1.5,
    backgroundColor: "rgba(0,0,0,0.02)",
  },
};

// Inner component that receives Formik field props and can use hooks
function CostCenterFieldInner({ field, form, availableCostCenters, showCostCenterSection }) {
  const availableIds = useMemo(() => new Set(availableCostCenters.map(cc => cc.id)), [
    availableCostCenters,
  ]);

  const prevProjectsRef = useRef(null);

  // Filter invalid cost centers when available options change
  useEffect(() => {
    const projectsKey = JSON.stringify([...availableIds].sort());
    if (prevProjectsRef.current === projectsKey) return;
    prevProjectsRef.current = projectsKey;

    const currentIds = field.value || [];
    const validIds = currentIds.filter(id => availableIds.has(id));

    if (validIds.length !== currentIds.length) {
      form.setFieldValue("costCenterIds", validIds);
    }
  }, [availableIds, field.value, form]);

  if (!showCostCenterSection) return null;

  return (
    <Grid sx={{ mt: 2 }} size={12}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5, fontWeight: 500, px: 1.75 }}>
        Cost Center Restrictions (Optional)
      </Typography>
      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mb: 1, display: "block", px: 1.75 }}
      >
        Leave empty to grant access to all cost centers. Select specific cost centers to restrict
        access.
      </Typography>
      <Autocomplete
        fullWidth
        multiple
        options={availableCostCenters}
        getOptionLabel={option => option.label}
        groupBy={option => option.projectName}
        value={availableCostCenters.filter(cc => (field.value || []).includes(cc.id))}
        onChange={(_, newValue) => {
          form.setFieldValue(
            "costCenterIds",
            newValue.map(cc => cc.id)
          );
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />

            {option.label}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={field.value?.length ? "" : "All cost centers (no restrictions)"}
            fullWidth
          />
        )}
      />
    </Grid>
  );
}

// Wrapper component that uses Formik's Field to get field/form props
function CostCenterField({
  values,
  getAvailableCostCenters,
  checkIfCostCenterRestrictionsEnabled,
}) {
  const availableCostCenters = getAvailableCostCenters(values.projectIds);
  const costCenterRestrictionsEnabled = checkIfCostCenterRestrictionsEnabled(values.projectIds);
  const showCostCenterSection = availableCostCenters.length > 0 && costCenterRestrictionsEnabled;

  return (
    <Field name="costCenterIds">
      {({ field, form }) => (
        <CostCenterFieldInner
          field={field}
          form={form}
          availableCostCenters={availableCostCenters}
          showCostCenterSection={showCostCenterSection}
        />
      )}
    </Field>
  );
}

export default function InviteForm({ projectId, client, onSuccess }) {
  const { data: { clients = [] } = [] } = useQuery(CLIENTS_AND_PROJECTS_QUERY);
  const [inviteUser] = useMutation(INVITE_USER_MUTATION);
  const [updateUserAccount] = useMutation(UPDATE_USER_ACCOUNT_MUTATION);
  const { notifySuccess, notifyError } = useNotification();
  const { user } = useAuth();
  const { settings } = user.company;
  const userRoles = GROUPED_ROLES(user.role);
  const isAdmin = useRoles(["admin"]);
  const apolloClient = useApolloClient();

  const [existingUser, setExistingUser] = useState(null);
  const [selectionState, setSelectionState] = useState({
    companyWide: false,
    allProjectsClientIds: [],
  });

  const isExistingUserMode = !!existingUser?.id;
  const existingUserDisplayName = existingUser?.name || existingUser?.email;

  const activeProjectIdsByClient = useMemo(
    () =>
      new Map(
        clients.map(currentClient => [
          currentClient.id,
          (currentClient.projects || []).filter(project => isAdmin || project.active).map(project => project.id),
        ])
      ),
    [clients, isAdmin]
  );

  const isAlreadyInvitedError = errors => {
    const emailError = (errors?.email || "").toString().toLowerCase();
    const submitError = (errors?.submit || "").toString().toLowerCase();
    return (
      emailError.includes("already") ||
      submitError.includes("already invited") ||
      submitError.includes("already been invited")
    );
  };

  const normalizeProjectIds = projectIdsValue => {
    if (projectIdsValue === "all") {
      const isProjectAllowed = project => isAdmin || project.active;
      return clients.flatMap(c => c.projects.filter(isProjectAllowed).map(p => p.id));
    }

    return (projectIdsValue || []).filter(id => !isClientAccessValue(id));
  };

  const selectedClientAccessIds = projectIdsValue => {
    if (projectIdsValue === "all") return [];

    return (projectIdsValue || []).map(clientIdFromAccessValue).filter(Boolean);
  };

  const buildAssignmentScope = projectIdsValue => {
    if (projectIdsValue === "all" || selectionState.companyWide) {
      return { companyWide: true };
    }

    const selectedProjectIds = normalizeProjectIds(projectIdsValue);
    const directClientIds = selectedClientAccessIds(projectIdsValue);
    const fullClientIds = selectionState.allProjectsClientIds.filter(clientId => {
      const activeProjectIds = activeProjectIdsByClient.get(clientId) || [];
      return activeProjectIds.length > 0 && activeProjectIds.every(id => selectedProjectIds.includes(id));
    });
    const coveredProjectIds = new Set(
      fullClientIds.flatMap(clientId => activeProjectIdsByClient.get(clientId) || [])
    );
    const explicitProjectIds = selectedProjectIds.filter(id => !coveredProjectIds.has(id));
    const scope = {};

    if (fullClientIds.length > 0) scope.allProjectsClientIds = fullClientIds;
    if (explicitProjectIds.length > 0) scope.projectIds = explicitProjectIds;
    if (directClientIds.length > 0) scope.clientIds = directClientIds;

    return Object.keys(scope).length > 0 ? scope : null;
  };

  const handleProjectSelectionAction = ({ type, clientId, value }) => {
    setSelectionState(prev => {
      if (type === "select_all") {
        return { companyWide: true, allProjectsClientIds: [] };
      }

      if (type === "clear_all") {
        return { companyWide: false, allProjectsClientIds: [] };
      }

      if (type === "group_select" && clientId) {
        return {
          companyWide: false,
          allProjectsClientIds: Array.from(new Set([...prev.allProjectsClientIds, clientId])),
        };
      }

      if (type === "group_clear" && clientId) {
        return {
          companyWide: false,
          allProjectsClientIds: prev.allProjectsClientIds.filter(id => id !== clientId),
        };
      }

      if (type === "options_change") {
        const selectedIds = normalizeProjectIds(value);
        return {
          companyWide: value === "all",
          allProjectsClientIds: prev.allProjectsClientIds.filter(currentClientId => {
            const activeProjectIds = activeProjectIdsByClient.get(currentClientId) || [];
            return activeProjectIds.length > 0 && activeProjectIds.every(id => selectedIds.includes(id));
          }),
        };
      }

      return prev;
    });
  };

  const loadExistingUserByEmail = async email => {
    const normalizedEmail = (email || "").trim().toLowerCase();
    if (!normalizedEmail) return null;

    const { data } = await apolloClient.query({
      query: COMPANY_USERS_QUERY,
      fetchPolicy: "network-only",
    });

    const match = (data?.companyUsers || []).find(
      u => (u.email || "").trim().toLowerCase() === normalizedEmail
    );
    if (!match?.id) return null;

    const { data: userData } = await apolloClient.query({
      query: FETCH_USER,
      variables: { id: match.id },
      fetchPolicy: "network-only",
    });

    return userData?.user || null;
  };

  const onSubmit = async ({ emails, ...values }, { setErrors, setStatus, setSubmitting }) => {
    setSubmitting(true);
    const assignmentScope = buildAssignmentScope(values.projectIds);
    const selectedProjectIds = normalizeProjectIds(values.projectIds);
    const selectedClientIds = clients
      .filter(currentClient => currentClient.projects.some(project => selectedProjectIds.includes(project.id)))
      .map(currentClient => currentClient.id)
      .concat(selectedClientAccessIds(values.projectIds));

    if (values.userId) {
      try {
        const response = await updateUserAccount({
          variables: {
            id: values.userId,
            projectIds: selectedProjectIds,
            clientIds: selectedClientIds,
            costCenterIds: values.costCenterIds || [],
            assignmentScope,
            role: values.role,
          },
        });
        const payload = Object.values(response.data || {})[0];
        if (payload?.errors) {
          setErrors(parseErrors(payload.errors));
          setStatus({ success: false });
        } else {
          notifySuccess("User access has been updated");
          setStatus({ success: true });
          if (onSuccess) onSuccess();
        }
      } catch (e) {
        notifyError(e?.message || "Failed to update user access");
        setStatus({ success: false });
      } finally {
        setSubmitting(false);
      }

      return;
    }

    const allEmails = parseEmails(emails);

    const results = {
      successful: [],
      failed: [],
    };

    for (const email of allEmails) {
      const variables = {
        ...values,
        email: email.trim(),
        projectIds: selectedProjectIds,
        clientIds: selectedClientIds,
        costCenterIds: values.costCenterIds || [],
        assignmentScope,
        userId: values.userId,
      };

      try {
        const response = await inviteUser({ variables });
        const payload = Object.values(response.data || {})[0];

        if (payload?.errors) {
          const parsed = parseErrors(payload.errors);

          if (isAlreadyInvitedError(parsed) && allEmails.length === 1) {
            const existing = await loadExistingUserByEmail(email);
            if (!existing?.id) {
              results.failed.push({ email, errors: parsed });
              setErrors(mapKeys(parsed, { user: "userId" }));
              notifyError("User already exists, but could not be loaded to update access.");
              break;
            }

            const unionProjectIds = Array.from(
              new Set([...(existing.projectIds || []), ...selectedProjectIds])
            );

            setExistingUser({ ...existing, projectIds: unionProjectIds });
            setErrors({});
            setStatus({ existingUser: true });
            setSubmitting(false);

            results.successful = [];
            results.failed = [];

            return;
          }

          results.failed.push({ email, errors: parsed });
        } else {
          results.successful.push(email);
        }
      } catch (error) {
        results.failed.push({ email, errors: { submit: error?.message || "Invite failed" } });
      }
    }

    if (results.successful.length > 0) {
      const message =
        results.successful.length === 1
          ? "User has been granted access"
          : `${results.successful.length} users have been granted access`;
      notifySuccess(message);
    }

    if (results.failed.length > 0) {
      const failedEmails = results.failed.map(f => f.email).join(", ");
      notifyError(`Failed to invite: ${failedEmails}`);

      if (results.failed[0]) {
        setErrors(mapKeys(results.failed[0].errors, { user: "userId" }));
      }
    }

    setSubmitting(false);

    if (results.successful.length > 0) {
      setStatus({ success: true });
      if (onSuccess) {
        onSuccess();
      }
    } else {
      setStatus({ success: false });
    }
  };

  const projectIds = client ? client.projects.map(project => project.id) : [];
  const initialValues = {
    emails: "",
    projectIds: projectId ? [projectId] : projectIds,
    costCenterIds: [],
    role: null,
    userId: null,
  };

  // Get available cost centers based on selected projects
  const getAvailableCostCenters = selectedProjectIds => {
    const normalizedIds = normalizeProjectIds(selectedProjectIds);
    const costCenters = [];
    clients.forEach(client => {
      client.projects.forEach(project => {
        if (normalizedIds.includes(project.id) && project.costCenters) {
          project.costCenters.forEach(cc => {
            costCenters.push({
              id: cc.id,
              label: cc.costCenterName || cc.costCenterCode || `Cost Center ${cc.id}`,
              projectName: project.name,
              projectId: project.id,
            });
          });
        }
      });
    });
    return costCenters;
  };

  // Check if any selected project has cost center restrictions enabled
  const checkIfCostCenterRestrictionsEnabled = selectedProjectIds => {
    const normalizedIds = normalizeProjectIds(selectedProjectIds);
    return clients.some(client =>
      client.projects.some(
        project =>
          normalizedIds.includes(project.id) && project.settings?.cost_center_user_restrictions
      )
    );
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={
        isExistingUserMode
          ? {
              ...initialValues,
              emails: existingUser?.email || "",
              userId: existingUser?.id || null,
              role: existingUser?.role || null,
              projectIds:
                existingUser?.projectIds && existingUser.projectIds.length > 0
                  ? existingUser.projectIds
                  : initialValues.projectIds,
              costCenterIds: existingUser?.costCenterIds || [],
            }
          : initialValues
      }
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ errors, handleBlur, isSubmitting, handleSubmit, values }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Input
                name="emails"
                label={isExistingUserMode ? "User Email" : "New User Email"}
                onBlur={handleBlur}
                type="autoExpand"
                disabled={isExistingUserMode}
                showNAButton={false}
                helperText="Enter one or more emails separated by commas or semicolons"
              />
            </Grid>
            {isExistingUserMode && (
              <Grid size={12}>
                <Box sx={styles.existingUserNotice}>
                  <strong>{existingUserDisplayName}</strong> is an existing user. You can update
                  their permissions below.
                </Box>
              </Grid>
            )}
            <Grid size={12}>
              <CollapsibleSelect
                size="small"
                name="role"
                label="Permission Role"
                options={userRoles}
                subheaderFontSize="1.0rem"
                value={values.role}
              />
            </Grid>
            {(!projectId || isExistingUserMode) && (
              <Grid size={12}>
                <ProjectAutocomplete
                  allProjects={isAdmin}
                  allowSelectAll
                  fullWidth
                  multiple
                  scrollToBottomOnOpen
                  group={!settings.warehouse_id_dropdown_menu}
                  includeClientAccessOptions
                  name="projectIds"
                  label={`${settings.filter_field2} Permissions`}
                  onSelectionAction={handleProjectSelectionAction}
                />
              </Grid>
            )}
            <CostCenterField
              values={values}
              getAvailableCostCenters={getAvailableCostCenters}
              checkIfCostCenterRestrictionsEnabled={checkIfCostCenterRestrictionsEnabled}
            />

            <Grid size={12}>
              {errors.submit && (
                <Box mt={3}>
                  <FormHelperText error>{errors.submit}</FormHelperText>
                </Box>
              )}
            </Grid>
          </Grid>
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              id="inviteUserSubmit"
              color="primary"
              disabled={isSubmitting}
              size="large"
              type="submit"
              variant="contained"
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isExistingUserMode ? "Update Access" : "Send Invite(s)"}
            </Button>
          </Box>
        </form>
      )}
    </Formik>
  );
}
